import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ShieldCheck,
  RefreshCw,
  KeyRound,
  ArrowRight,
} from 'lucide-react';
import { auth } from '../lib/firebase';
import { PairUpLogo } from '../components/common/PairUpLogo';
import {
  fetchV2Bookings,
  cancelV2Booking,
  getBookingCompletionOtp,
  sendMeetingStartOtp,
} from '../lib/bookingService';

export interface ClientBookingItem {
  id: string;
  bookingId?: string;
  clientId: string;
  clientName?: string;
  companionId: string;
  companionName: string;
  companionImage?: string;
  date: string;
  time: string;
  durationHours: number;
  occasion?: string;
  meetingLocation: string;
  city?: string;
  price?: number;
  totalAmount?: number;
  gross_amount?: number;
  status: string;
  meetingStartedAt?: string;
  countdownEndsAt?: string;
  createdAt?: any;
}

export const MyBookingsPage: React.FC = () => {

  const [bookings, setBookings] = useState<ClientBookingItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled'>('all');

  // Cancel booking modal
  const [cancelTargetBooking, setCancelTargetBooking] = useState<ClientBookingItem | null>(null);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [notice, setNotice] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // OTP reveal modal
  const [otpBooking, setOtpBooking] = useState<ClientBookingItem | null>(null);
  const [otpNotice, setOtpNotice] = useState<string | null>(null);

  const clientUid = auth.currentUser?.uid;

  const loadClientBookings = async () => {
    setIsLoading(true);
    setNotice(null);

    const loadedList: ClientBookingItem[] = [];
    try {
      const res = await fetchV2Bookings({ clientId: clientUid || undefined });
      if (!res.success) throw new Error(res.error || 'Bookings are unavailable.');
      if (Array.isArray(res.bookings)) {
        for (const b of res.bookings) {
          loadedList.push({
            id: b.id, bookingId: b.id, clientId: b.client_id, clientName: b.client_name,
            companionId: b.companion_id, companionName: b.companion_name,
            companionImage: b.companion_image, date: b.booking_date, time: b.start_time,
            durationHours: b.duration_hours, occasion: b.occasion,
            meetingLocation: b.location_name, totalAmount: b.gross_amount,
            status: b.status as ClientBookingItem['status'],
            meetingStartedAt: (b as any).meeting_started_at,
            countdownEndsAt: (b as any).countdown_ends_at,
            createdAt: b.created_at,
          });
        }
      }
    } catch (err: any) {
      setNotice({ message: err?.message || 'Bookings could not be loaded from the server.', type: 'error' });
    }

    // Sort by date or id descending
    loadedList.sort((a, b) => b.id.localeCompare(a.id));
    setBookings(loadedList);
    setIsLoading(false);
  };

  useEffect(() => {
    loadClientBookings();
  }, [clientUid]);

  const handleCancelBooking = async () => {
    if (!cancelTargetBooking) return;
    if (!cancelReason.trim()) {
      alert('Please provide a reason for cancelling this booking.');
      return;
    }

    setIsCancelling(true);
    try {
      const result = await cancelV2Booking(cancelTargetBooking.id, 'client', cancelReason.trim());
      if (!result.success) throw new Error(result.error || 'Cancellation is unavailable.');
      setNotice({ message: result.message || `Booking #${cancelTargetBooking.id} cancelled.`, type: 'success' });
      setCancelTargetBooking(null); setCancelReason('');
      await loadClientBookings();
    } catch (err: any) {
      setNotice({ message: err.message || 'Failed to cancel booking.', type: 'error' });
    } finally {
      setIsCancelling(false);
    }
  };

  const requestMeetupStartCode = async (booking: ClientBookingItem) => {
    setOtpBooking(booking);
    setOtpNotice(null);
    const result = await sendMeetingStartOtp(booking.id);
    setOtpNotice(
      result.success
        ? "Meeting Start OTP sent via APITXT SMS to your registered mobile number. Share this 6-digit code with your companion when they ask 'Enter Your Meeting Start OTP' to begin the meeting countdown."
        : result.error || 'The Meeting Start OTP could not be sent.'
    );
  };

  const requestMeetupCode = async (booking: ClientBookingItem) => {
    setOtpBooking(booking); setOtpNotice(null);
    const result = await getBookingCompletionOtp(booking.id, booking.clientId);
    setOtpNotice(result.success
      ? 'A Meeting Completion OTP was sent via APITXT SMS to the phone number registered to your account. Share it only after the meetup duration is complete.'
      : result.error || 'The verification code could not be sent.');
  };

  // Filtered list
  const filteredBookings = bookings.filter((b) => {
    if (filterStatus === 'all') return true;
    const s = b.status?.toLowerCase();
    if (filterStatus === 'pending') return s.includes('pending');
    if (filterStatus === 'confirmed') return s.includes('confirmed') || s.includes('accepted');
    if (filterStatus === 'completed') return s.includes('completed');
    if (filterStatus === 'cancelled') return s.includes('cancelled') || s.includes('declined');
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col selection:bg-pink-500 selection:text-white">
      {/* Top Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-2xs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2.5">
            <PairUpLogo className="w-9 h-9 shrink-0 drop-shadow-xs" />
            <div className="flex flex-col">
              <span className="text-xl font-black tracking-tight text-slate-900 leading-none">
                PairUp<span className="text-pink-600">Today</span>
              </span>
              <span className="text-[10px] font-bold text-slate-400">My Bookings</span>
            </div>
          </Link>

          <div className="flex items-center gap-2.5">
            <Link
              to="/home"
              className="px-3.5 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 rounded-full hover:bg-slate-100 transition-colors"
            >
              Explore Companions
            </Link>
            <Link
              to="/my-account"
              className="px-3.5 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
            >
              My Account
            </Link>
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6 w-full">
        {/* Banner Alert */}
        {notice && (
          <div
            className={`p-4 rounded-2xl text-xs font-bold flex items-center gap-2.5 shadow-xs ${
              notice.type === 'success'
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                : 'bg-rose-50 text-rose-800 border border-rose-200'
            }`}
          >
            {notice.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span>{notice.message}</span>
          </div>
        )}

        {/* Page Title & Status Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">
              My Companion Bookings
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Real reservation records stored securely in Cloud Firestore for your account.
            </p>
          </div>

          <button
            type="button"
            onClick={loadClientBookings}
            className="text-xs font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-xl shadow-2xs cursor-pointer self-start sm:self-auto"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh Bookings</span>
          </button>
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap gap-2 text-xs font-bold">
          {(['all', 'pending', 'confirmed', 'completed', 'cancelled'] as const).map((st) => (
            <button
              key={st}
              type="button"
              onClick={() => setFilterStatus(st)}
              className={`px-3.5 py-1.5 rounded-full capitalize transition-all cursor-pointer ${
                filterStatus === st
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {st} ({st === 'all' ? bookings.length : bookings.filter((b) => b.status?.toLowerCase().includes(st)).length})
            </button>
          ))}
        </div>

        {/* Loading Spinner */}
        {isLoading && (
          <div className="py-20 text-center space-y-3">
            <div className="w-8 h-8 border-3 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-xs font-bold text-slate-500">Loading your real bookings from Firestore...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredBookings.length === 0 && (
          <div className="p-12 text-center bg-white rounded-3xl border border-slate-200 space-y-4 shadow-xs">
            <div className="w-14 h-14 rounded-2xl bg-pink-50 text-pink-500 flex items-center justify-center mx-auto">
              <Calendar className="w-7 h-7" />
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-black text-slate-900">No bookings found</h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                {filterStatus === 'all'
                  ? "You haven't made any companion bookings yet. Find and book verified companions near you!"
                  : `No bookings currently matching status "${filterStatus}".`}
              </p>
            </div>
            <div className="pt-2">
              <Link
                to="/home"
                className="px-5 py-2.5 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-bold text-xs rounded-xl shadow-md transition-all inline-flex items-center gap-1.5"
              >
                <span>Find a Companion Near You</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        {/* Bookings List Cards */}
        {!isLoading && filteredBookings.length > 0 && (
          <div className="space-y-4">
            {filteredBookings.map((b) => {
              const s = (b.status || 'pending').toLowerCase();
              const isPending = s.includes('pending');
              const isConfirmed = s.includes('confirmed') || s.includes('accepted');
              const isCompleted = s.includes('completed');
              const isCancelled = s.includes('cancelled') || s.includes('declined');

              return (
                <div
                  key={b.id}
                  className="bg-white rounded-3xl border border-slate-200 shadow-xs hover:shadow-md transition-all p-5 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-5"
                >
                  {/* Left: Companion & Details */}
                  <div className="flex items-start sm:items-center gap-4">
                    <div className="w-16 h-16 sm:w-20 sm:y-20 rounded-2xl overflow-hidden border-2 border-slate-100 shrink-0 bg-slate-100 shadow-2xs">
                      <img
                        src={b.companionImage}
                        alt={b.companionName}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-black text-pink-600 bg-pink-50 px-2 py-0.5 rounded border border-pink-200">
                          #{b.bookingId || b.id}
                        </span>
                        <h3 className="text-base font-black text-slate-900 tracking-tight">
                          {b.companionName}
                        </h3>

                        {/* Status Badge */}
                        {isConfirmed && (
                          <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-black rounded-full uppercase border border-emerald-300">
                            Confirmed
                          </span>
                        )}
                        {isPending && (
                          <span className="px-2.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-black rounded-full uppercase border border-amber-300">
                            Pending Confirmation
                          </span>
                        )}
                        {isCompleted && (
                          <span className="px-2.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-black rounded-full uppercase border border-blue-300">
                            Completed
                          </span>
                        )}
                        {isCancelled && (
                          <span className="px-2.5 py-0.5 bg-rose-100 text-rose-800 text-[10px] font-black rounded-full uppercase border border-rose-300">
                            Cancelled
                          </span>
                        )}
                      </div>

                      <p className="text-xs font-semibold text-slate-700">
                        {[b.occasion, b.durationHours ? `${b.durationHours} Hours` : undefined].filter(Boolean).join(' • ') || 'Booking details unavailable'}
                      </p>

                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 pt-0.5">
                        <span className="flex items-center gap-1 font-medium">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          {[b.date, b.time].filter(Boolean).join(' at ') || 'Schedule unavailable'}
                        </span>
                        <span className="flex items-center gap-1 font-medium">
                          <MapPin className="w-3.5 h-3.5 text-slate-400" />
                          {b.meetingLocation || 'Location unavailable'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Price & Actions */}
                  <div className="flex flex-row md:flex-col items-center md:items-end justify-between border-t md:border-t-0 pt-3 md:pt-0 border-slate-100 gap-3">
                    <div className="text-left md:text-right">
                      <span className="text-[10px] text-slate-400 font-bold block uppercase">Total Amount</span>
                      <span className="text-base font-black text-slate-900 font-mono">
                      {typeof b.totalAmount === 'number' ? `₹${b.totalAmount.toLocaleString('en-IN')}` : '—'}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {/* View Booking Detail */}
                      <Link
                        to={`/booking/${b.id}`}
                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl transition-colors"
                      >
                        Details
                      </Link>

                      {/* Let's Start Meeting button (generates Meeting Start OTP sent to client via APITXT) */}
                      {isConfirmed && !s.includes('in_progress') && (
                        <button
                          type="button"
                          onClick={() => void requestMeetupStartCode(b)}
                          className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold rounded-xl border border-emerald-200 transition-colors flex items-center gap-1 cursor-pointer"
                        >
                          <Clock className="w-3.5 h-3.5" />
                          <span>Let&apos;s Start Meeting</span>
                        </button>
                      )}

                      {/* Reveal Completion OTP button (for confirmed / in-progress bookings) */}
                      {(isConfirmed || s.includes('in_progress')) && (
                        <button
                          type="button"
                          onClick={() => void requestMeetupCode(b)}
                          className="px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-bold rounded-xl border border-purple-200 transition-colors flex items-center gap-1 cursor-pointer"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                          <span>Send Completion OTP</span>
                        </button>
                      )}

                      {/* Cancel button if pending or confirmed */}
                      {(isPending || isConfirmed) && (
                        <button
                          type="button"
                          onClick={() => setCancelTargetBooking(b)}
                          className="px-3 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-50 rounded-xl border border-rose-200 transition-colors cursor-pointer"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* OTP Display Modal */}
      {otpBooking && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl border border-slate-200 max-w-sm w-full p-6 space-y-4 shadow-2xl text-center">
            <div className="w-12 h-12 rounded-2xl bg-purple-100 text-purple-700 flex items-center justify-center mx-auto">
              <KeyRound className="w-6 h-6" />
            </div>

            <div><h3 className="text-base font-black text-slate-900">Meetup verification</h3>
              <p className="text-xs text-slate-600 mt-2">{otpNotice || `Requesting a secure code for ${otpBooking.companionName}…`}</p></div>

            <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl text-[11px] text-purple-900 text-left">
              🔒 Verification and any payout status are determined by the secure server workflow.
            </div>

            <button
              type="button"
              onClick={() => setOtpBooking(null)}
              className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl transition-colors cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Cancel Booking Confirmation Modal */}
      {cancelTargetBooking && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl border border-slate-200 max-w-md w-full p-6 space-y-4 shadow-2xl">
            <div className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center mx-auto">
              <XCircle className="w-6 h-6" />
            </div>

            <div className="text-center space-y-1">
              <h3 className="text-base font-black text-slate-900">Cancel Booking</h3>
              <p className="text-xs text-slate-500">
                Are you sure you want to cancel booking #{cancelTargetBooking.id} with{' '}
                <strong>{cancelTargetBooking.companionName}</strong>?
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-800">
                Reason for cancellation <span className="text-rose-500">*</span>
              </label>
              <textarea
                rows={3}
                required
                placeholder="Please state why you are cancelling this booking..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-rose-500 focus:bg-white resize-none"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setCancelTargetBooking(null)}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl transition-colors cursor-pointer"
              >
                Keep Booking
              </button>
              <button
                type="button"
                disabled={isCancelling || !cancelReason.trim()}
                onClick={handleCancelBooking}
                className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors cursor-pointer disabled:opacity-40"
              >
                {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-6 text-center text-xs text-slate-400 border-t border-slate-200 mt-10 bg-white">
        PairUpToday Client Portal • Protected by PairUpToday Meetup Verification
      </footer>
    </div>
  );
};

export default MyBookingsPage;
